// AiProvenancePills.jsx

import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import PlanesMark from 'components/logo/mark/PlanesMark';

// CSS
import './AiProvenancePills.css';

const CONNECTED_TYPES = new Set([
  'airtable',
  'calendar',
  'clickup',
  'drive',
  'dropbox',
  'gmail',
  'notion',
]);

function cleanString(value) {
  return String(value || '').trim();
}

function permalinkLabelForUrl(url) {
  const href = cleanString(url);
  if (!href) return 'Link';
  try {
    const parsed = new URL(href);
    return parsed.hostname.replace(/^www\./, '') || 'Link';
  } catch {
    return 'Link';
  }
}

function citationNodeKey(citation) {
  return cleanString(citation?.nodeKey || citation?.sourceNodeKey);
}

function citationSourceType(citation) {
  const raw = cleanString(citation?.sourceType).toLowerCase();
  if (CONNECTED_TYPES.has(raw)) return raw;
  if (raw.startsWith('airtable_')) return 'airtable';
  if (raw.startsWith('notion_')) return 'notion';
  const typeKey = cleanString(citation?.type).toLowerCase();
  if (typeKey.startsWith('airtable_tbl_')) return 'airtable';
  if (typeKey === 'notion_page' || typeKey.startsWith('notion_db_')) return 'notion';
  const permalink = cleanString(citation?.permalink || citation?.sourceUrl).toLowerCase();
  if (permalink.includes('airtable.com')) return 'airtable';
  if (
    permalink.includes('notion.so')
    || permalink.includes('notion.site')
    || permalink.includes('notion.com')
  ) {
    return 'notion';
  }
  if (permalink.includes('dropbox.com')) return 'dropbox';
  if (permalink.includes('mail.google.com')) return 'gmail';
  if (permalink.includes('drive.google.com') || permalink.includes('docs.google.com')) {
    return 'drive';
  }
  if (
    permalink.includes('calendar.google.com')
    || permalink.includes('google.com/calendar')
  ) {
    return 'calendar';
  }
  if (permalink.includes('app.clickup.com')) return 'clickup';
  return 'appdeck';
}

function sourceDisplayName(sourceType) {
  if (sourceType === 'appdeck') return 'Appdeck';
  const key = cleanString(sourceType);
  if (!key) return 'Source';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function chunkHeading(chunk, index) {
  const pageLabel = cleanString(chunk?.pageLabel);
  if (pageLabel) return pageLabel;
  const sheetName = cleanString(chunk?.sheetName);
  if (sheetName) return sheetName;
  const kind = cleanString(chunk?.kind).toLowerCase();
  if (kind === 'document') return `Passage ${index + 1}`;
  if (kind === 'media') return `OCR ${index + 1}`;
  if (kind === 'body') return `Excerpt ${index + 1}`;
  if (kind === 'schema') return 'Fields';
  return `Passage ${index + 1}`;
}

function SourcePillIcon({ sourceType, color }) {
  if (sourceType === 'appdeck') {
    return (
      <span className="ai-provenance-pills__icon ai-provenance-pills__icon--mark" aria-hidden>
        <PlanesMark size={14} color={color} variant="solid" />
      </span>
    );
  }

  return (
    <span className="ai-provenance-pills__icon ai-provenance-pills__icon--fallback" aria-hidden>
      {sourceDisplayName(sourceType).charAt(0) || '?'}
    </span>
  );
}

/**
 * Compact per-source provenance pills. Click reveals that source's chunks.
 */
const AiProvenancePills = React.memo(({
  citations = [],
  onOpenRecord = null,
  onOpenOrigin = null,
}) => {
  const { theme } = useTheme();
  const listId = useId();
  const rootRef = useRef(null);
  const [openKey, setOpenKey] = useState('');

  const rows = (Array.isArray(citations) ? citations : [])
    .map((citation) => {
      const nodeKey = citationNodeKey(citation);
      if (!nodeKey) return null;
      const chunks = Array.isArray(citation?.chunks) ? citation.chunks : [];
      const fallbackText = cleanString(citation?.excerpt);
      const resolvedChunks = chunks.length
        ? chunks.filter((chunk) => cleanString(chunk?.text))
        : (fallbackText ? [{ text: fallbackText, kind: 'excerpt' }] : []);
      const sourceType = citationSourceType(citation);
      return {
        citation,
        nodeKey,
        sourceType,
        sourceName: sourceDisplayName(sourceType),
        title: cleanString(citation?.title) || 'Source',
        typeLabel: cleanString(citation?.typeLabel),
        permalink: cleanString(citation?.permalink || citation?.sourceUrl),
        chunks: resolvedChunks,
        chunkCount: resolvedChunks.length,
      };
    })
    .filter(Boolean);

  const closePanel = useCallback(() => {
    setOpenKey('');
  }, []);

  useEffect(() => {
    if (!openKey) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closePanel();
    };
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closePanel();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [openKey, closePanel]);

  if (!rows.length) return null;

  return (
    <div
      ref={rootRef}
      className="ai-provenance-pills"
      id={listId}
    >
      <div className="ai-provenance-pills__row" role="list" aria-label="Sources used">
        {rows.map((row) => {
          const isOpen = openKey === row.nodeKey;
          const panelId = `${listId}-${row.nodeKey}`;
          return (
            <div
              key={row.nodeKey}
              className="ai-provenance-pills__item"
              role="listitem"
            >
              <button
                type="button"
                className={`ai-provenance-pills__pill${isOpen ? ' is-open' : ''}`}
                style={{
                  borderColor: theme.outlineMuted || theme.outline || theme.backgroundHover,
                  background: isOpen
                    ? (theme.backgroundHover || theme.background)
                    : theme.background,
                  color: theme.foreground,
                }}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => {
                  setOpenKey(isOpen ? '' : row.nodeKey);
                }}
              >
                <SourcePillIcon sourceType={row.sourceType} color={theme.foreground} />
                <span className="ai-provenance-pills__title">{row.sourceName}</span>
                {row.chunkCount > 1 ? (
                  <span
                    className="ai-provenance-pills__count"
                    style={{ color: theme.foregroundMuted }}
                  >
                    {row.chunkCount}
                  </span>
                ) : null}
              </button>

              {isOpen ? (
                <div
                  id={panelId}
                  className="ai-provenance-pills__panel"
                  style={{
                    background: theme.background,
                    borderColor: theme.outlineMuted || theme.outline || theme.backgroundHover,
                    color: theme.foreground,
                    boxShadow: theme.elevationShadowSoft || theme.elevationShadow || undefined,
                  }}
                  role="region"
                  aria-label={`${row.sourceName} passages`}
                >
                  <div className="ai-provenance-pills__panel-head">
                    <span className="ai-provenance-pills__panel-title">{row.sourceName}</span>
                    {row.title ? (
                      <span
                        className="ai-provenance-pills__panel-meta"
                        style={{ color: theme.foregroundMuted }}
                      >
                        {row.title}
                      </span>
                    ) : null}
                  </div>

                  {row.chunks.length ? (
                    <div className="ai-provenance-pills__chunks">
                      {row.chunks.map((chunk, index) => (
                        <div
                          key={cleanString(chunk.chunkId) || `${row.nodeKey}-${index}`}
                          className="ai-provenance-pills__chunk"
                          style={{
                            borderColor: theme.outlineMuted || theme.outline || theme.backgroundHover,
                          }}
                        >
                          <div
                            className="ai-provenance-pills__chunk-label"
                            style={{ color: theme.foregroundMuted }}
                          >
                            {chunkHeading(chunk, index)}
                          </div>
                          <div className="ai-provenance-pills__chunk-text">
                            {cleanString(chunk.text)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p
                      className="ai-provenance-pills__empty"
                      style={{ color: theme.foregroundMuted }}
                    >
                      No retrieved passage for this source — the answer used the record itself.
                    </p>
                  )}

                  <div className="ai-provenance-pills__actions">
                    {typeof onOpenRecord === 'function' ? (
                      <button
                        type="button"
                        className="ai-provenance-pills__action"
                        style={{ color: theme.foreground }}
                        onClick={() => onOpenRecord(row.citation)}
                      >
                        Open in Appdeck
                      </button>
                    ) : null}
                    {row.permalink && typeof onOpenOrigin === 'function' ? (
                      <button
                        type="button"
                        className="ai-provenance-pills__action ai-provenance-pills__action--origin"
                        style={{ color: theme.foreground }}
                        onClick={() => onOpenOrigin(row.permalink, row.citation)}
                      >
                        {permalinkLabelForUrl(row.permalink)}
                      </button>
                    ) : row.permalink ? (
                      <a
                        className="ai-provenance-pills__action ai-provenance-pills__action--origin"
                        style={{ color: theme.foreground }}
                        href={row.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {permalinkLabelForUrl(row.permalink)}
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
});

AiProvenancePills.displayName = 'AiProvenancePills';

export default AiProvenancePills;
